import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';

import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {catchError} from 'rxjs/internal/operators/catchError';

import {HttpStatusCodes} from '../enums';
import {IReportingModuleConfig} from '../interfaces';

import {ApiError} from './api-error';
import {ApiResponse} from './api-response';

export abstract class BaseApiService {
    private readonly apiUrl: string;

    protected httpHeaders: HttpHeaders;

    protected constructor(protected moduleConfig: IReportingModuleConfig, protected httpClient: HttpClient) {
        this.apiUrl = this.getApiUrl();

        this.httpHeaders = new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        });
    }

    static handleError(error: HttpErrorResponse): Observable<ApiResponse<ApiError[] | null>> {
        const apiErrorResponse = new ApiResponse<ApiError[]>(error.status, [], []);

        /**
         * Handle common errors
         */
        if (!error.error || (error.status === HttpStatusCodes.Unknown)) {
            const errorModel = new ApiError();

            errorModel.status = error.status;
            errorModel.data = error.statusText;

            apiErrorResponse.appendMessage(error.statusText);
            apiErrorResponse.setData([errorModel]);

            return of(apiErrorResponse);
        }

        /**
         * Handle custom API error.
         */
        if (typeof error.error === 'object') {
            const status = error.error.hasOwnProperty('status') && error.error.status;
            const traceId = error.error.hasOwnProperty('traceId') && error.error.traceId;
            const type = error.error.hasOwnProperty('type') && error.error.type;
            const errors = error.error.hasOwnProperty('errors') && error.error.errors;

            const subError = errors && errors.Error && errors.Error.length > 0
                ? errors.Error[0]
                : null;

            const data = subError || (error.error.hasOwnProperty('title') && error.error.title);

            const errorModel = new ApiError(status, data, traceId, type);

            if (!errorModel.status) {
                errorModel.status = error.status;
            }

            apiErrorResponse.appendMessage(errorModel.data);
            apiErrorResponse.setData([errorModel]);
        }

        return of(apiErrorResponse);
    }

    /**
     * Custom http get request wrapper.
     *
     * OutgoingDataType - the type of the response data
     *
     * @param path
     * - API route, which will be concatenate with the API url
     *
     * @param cacheableResponse
     * - Set custom header, which is used only
     * - in the basic http interceptor(http-api.interceptor.ts)
     * - The header indicates, that the response of this request
     * - must be cached.
     */
    protected get<OutgoingDataType>(
        path: string,
        cacheableResponse = false
    ): Observable<ApiResponse<ApiError[] | OutgoingDataType>> {
        const httpHeaders: HttpHeaders = this.httpHeaders;

        return this.httpClient
            .get(this.apiUrl + path, {
                headers: httpHeaders
            })
            .pipe(
                map((result: OutgoingDataType) => {
                    return new ApiResponse<OutgoingDataType>(HttpStatusCodes.Ok, result);
                }),
                catchError(BaseApiService.handleError)
            );
    }

    /**
     * Custom http post request wrapper.
     *
     * IncomingDataType - the type of the data, which to be post
     * OutgoingDataType - the type of the response data
     *
     * @param path
     * - API route, which will be concatenate with the API url
     *
     * @param data
     * - The data, which will be post to the API.
     *
     * @param cacheableResponse
     * - Set custom header, which is used only
     * - in the basic http interceptor(http-api.interceptor.ts)
     * - The header indicates, that the response of this request
     * - must be cached.
     *
     * @param expectsBlob
     * - Set custom header, which is used only
     * - when is expected the response to be blob
     */
    protected post<IncomingDataType, OutgoingDataType>(
        path: string,
        data: IncomingDataType,
        cacheableResponse = false,
        expectsBlob = false,
        apiVersion: string = null,
    ): Observable<ApiResponse<ApiError[] | OutgoingDataType>> {
        const httpHeaders: HttpHeaders = this.httpHeaders;

        let options = {};

        if (expectsBlob) {
            options = { headers: httpHeaders, responseType: 'blob' as const };
        } else {
            options = {headers: httpHeaders, responseType: 'json' as const};
        }
        const apiBaseUrl = this.getApiUrl() + apiVersion + '/';

        return this.httpClient
            .post(apiBaseUrl + path, data, options)
            .pipe(
                map((result: OutgoingDataType) => {
                    return new ApiResponse<OutgoingDataType>(HttpStatusCodes.Ok, result);
                }),
                catchError(BaseApiService.handleError)
            );
    }

    protected abstract getApiUrl(): string;
}
